import React, { useState, useEffect } from 'react';
import { crmAPI } from '../../lib/api';
import { 
    Plus, Edit, Trash2, Search, RefreshCw, FileText, 
    Download, Eye, Send, CheckCircle, XCircle, Clock, Merge
} from 'lucide-react';

const Invoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [projectError, setProjectError] = useState('');
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [merging, setMerging] = useState(false);
    
    // For generating invoice
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectLifts, setProjectLifts] = useState([]);
    const [selectedLifts, setSelectedLifts] = useState([]);
    const [generating, setGenerating] = useState(false);
    
    // Form data for manual invoice
    const [formData, setFormData] = useState({
        client_id: '',
        project_id: '',
        invoice_date: new Date().toISOString().split('T')[0],
        billing_month: '',
        billing_year: new Date().getFullYear(),
        billing_month_num: new Date().getMonth() + 1,
        work_order_no: '',
        work_order_date: '',
        noa_ref: '',
        noa_date: '',
        coa_ref: '',
        coa_date: '',
        notes: '',
    });

    const [invoiceItems, setInvoiceItems] = useState([]);

    // Merge form data
    const [mergeFormData, setMergeFormData] = useState({
        invoice_date: new Date().toISOString().split('T')[0],
        billing_month: '',
        billing_year: new Date().getFullYear(),
        billing_month_num: new Date().getMonth() + 1,
        mark_as_merged: true,
    });

    useEffect(() => { 
        fetchInvoices(); 
        fetchProjects();
    }, []);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const response = await crmAPI.getInvoices(1, { search: searchTerm });
            // Handle InvoiceCollection paginated response: { success: true, data: { data: [...], meta: {...} } }
            const responseData = response.data;
            const invoicesData = responseData?.data?.data || responseData?.data || [];
            setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
        } catch (err) { 
            console.error('Failed to load invoices:', err);
            setError('Failed to load invoices: ' + (err.message || 'Unknown error'));
            setInvoices([]);
        }
        finally { 
            setLoading(false); 
        }
    };

    const fetchProjects = async () => {
        try {
            const response = await crmAPI.getProjects();
            setProjects(response.data.data || response.data);
        } catch (err) {
            console.error('Failed to load projects');
        }
    };

    const fetchProjectLifts = async (projectId) => {
        setProjectError('');
        try {
            console.log('Fetching lifts for project:', projectId);
            const response = await crmAPI.getProjectLifts(projectId);
            console.log('Project lifts response:', response);
            setProjectLifts(response.data.data || response.data || []);
        } catch (err) {
            console.error('Error fetching project lifts:', err);
            setProjectError(err.response?.data?.message || err.message || 'Failed to load lifts');
            setProjectLifts([]);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchInvoices();
    };

    const handleProjectSelect = (projectId) => {
        setSelectedProject(projectId);
        setSelectedLifts([]);
        if (projectId) {
            fetchProjectLifts(projectId);
        } else {
            setProjectLifts([]);
        }
    };

    const toggleLiftSelection = (liftId) => {
        setSelectedLifts(prev => 
            prev.includes(liftId) 
                ? prev.filter(id => id !== liftId)
                : [...prev, liftId]
        );
    };

    const handleGenerateFromLifts = async () => {
        if (!selectedProject || selectedLifts.length === 0) {
            setError('Please select a project and at least one lift');
            return;
        }

        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        setGenerating(true);
        setError('');
        try {
            console.log('Generating invoice with:', { project_id: selectedProject, lift_ids: selectedLifts });
            const response = await crmAPI.generateInvoiceFromLifts({
                project_id: selectedProject,
                invoice_date: new Date().toISOString().split('T')[0],
                billing_month: months[new Date().getMonth()],
                billing_year: new Date().getFullYear(),
                billing_month_num: new Date().getMonth() + 1,
                lift_ids: selectedLifts,
            });
            console.log('Invoice generated successfully:', response);
            
            setShowGenerateModal(false);
            setSelectedProject(null);
            setSelectedLifts([]);
            setProjectLifts([]);
            fetchInvoices();
        } catch (err) {
            console.error('Error generating invoice:', err);
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to generate invoice: ' + (err.message || 'Unknown error'));
        }
        finally {
            setGenerating(false);
        }
    };

    const handleViewInvoice = async (invoice) => {
        try {
            const response = await crmAPI.getInvoice(invoice.id);
            // Handle InvoiceResource response: { success: true, data: {...} }
            setSelectedInvoice(response.data.data);
            setShowViewModal(true);
        } catch (err) {
            console.error('Error loading invoice:', err);
            setError('Failed to load invoice details: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this invoice?')) return;
        try {
            await crmAPI.deleteInvoice(id);
            fetchInvoices();
        } catch (err) {
            setError('Failed to delete invoice');
        }
    };

    const toggleInvoiceSelection = (id) => {
        setSelectedInvoices(prev => 
            prev.includes(id) 
                ? prev.filter(invId => invId !== id)
                : [...prev, id]
        );
    };

    const handleMergeInvoices = async () => {
        if (selectedInvoices.length < 2) {
            setError('Please select at least 2 invoices to merge');
            return;
        }

        // Check if all selected invoices belong to the same project
        const selectedInvs = invoices.filter(inv => selectedInvoices.includes(inv.id));
        const firstProject = selectedInvs[0]?.project_id;
        const allSameProject = selectedInvs.every(inv => inv.project_id === firstProject);
        
        if (!allSameProject) {
            setError('All selected invoices must belong to the same project');
            return;
        }

        // Check if any invoice is paid
        const hasPaidInvoice = selectedInvs.some(inv => inv.status === 'paid');
        if (hasPaidInvoice) {
            setError('Cannot merge invoices that are already paid. Please reverse payments first.');
            return;
        }

        setMerging(true);
        setError('');
        try {
            const response = await crmAPI.mergeInvoices({
                invoice_ids: selectedInvoices,
                invoice_date: mergeFormData.invoice_date,
                billing_month: mergeFormData.billing_month,
                billing_year: mergeFormData.billing_year,
                billing_month_num: mergeFormData.billing_month_num,
                mark_as_merged: mergeFormData.mark_as_merged,
            });
            
            if (response.data.success) {
                setShowMergeModal(false);
                setSelectedInvoices([]);
                setMergeFormData({
                    invoice_date: new Date().toISOString().split('T')[0],
                    billing_month: '',
                    billing_year: new Date().getFullYear(),
                    billing_month_num: new Date().getMonth() + 1,
                    mark_as_merged: true,
                });
                fetchInvoices();
            } else {
                setError(response.data.message || 'Failed to merge invoices');
            }
        } catch (err) {
            console.error('Error merging invoices:', err);
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to merge invoices');
        } finally {
            setMerging(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            draft: 'bg-gray-100 text-gray-800',
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-blue-100 text-blue-800',
            paid: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800',
            merged: 'bg-purple-100 text-purple-800',
        };
        
        const icons = {
            draft: <Clock className="w-3 h-3 mr-1" />,
            pending: <Clock className="w-3 h-3 mr-1" />,
            approved: <CheckCircle className="w-3 h-3 mr-1" />,
            paid: <CheckCircle className="w-3 h-3 mr-1" />,
            cancelled: <XCircle className="w-3 h-3 mr-1" />,
            merged: <Merge className="w-3 h-3 mr-1" />,
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
                {icons[status]}
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-BD', { 
            style: 'currency', 
            currency: 'BDT',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div>
            {/* Header */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Service Invoices</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage service & maintenance billing</p>
                </div>
                <div className="mt-4 sm:mt-0 flex gap-2">
                    <button 
                        onClick={() => {
                            if (selectedInvoices.length < 2) {
                                setError('Please select at least 2 invoices to merge');
                                return;
                            }
                            // Set default billing month
                            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                            setMergeFormData({
                                ...mergeFormData,
                                billing_month: months[new Date().getMonth()],
                            });
                            setShowMergeModal(true);
                        }}
                        disabled={selectedInvoices.length < 2}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Select 2+ invoices to merge"
                    >
                        <Merge className="w-4 h-4 mr-2" /> Merge ({selectedInvoices.length})
                    </button>
                    <button 
                        onClick={() => setShowGenerateModal(true)}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                    >
                        <Plus className="w-4 h-4 mr-2" /> Generate from Lifts
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                    <button onClick={() => setError('')} className="float-right font-bold">&times;</button>
                </div>
            )}

            {/* Search */}
            <form onSubmit={handleSearch} className="mb-4 flex gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input 
                        type="text" 
                        placeholder="Search by invoice number or reference..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" 
                    />
                </div>
                <button type="submit" className="px-4 py-2 bg-gray-200 dark:bg-slate-600 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500">
                    <RefreshCw className="w-5 h-5" />
                </button>
            </form>

            {/* Service Invoices Table */}
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase w-10">
                                        <input 
                                            type="checkbox"
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedInvoices(invoices.map(inv => inv.id));
                                                } else {
                                                    setSelectedInvoices([]);
                                                }
                                            }}
                                            checked={selectedInvoices.length > 0 && selectedInvoices.length === invoices.length}
                                            className="rounded border-gray-300"
                                        />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Invoice #</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Ref Number</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Project</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Billing Month</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {invoices.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                            No invoices found. Generate your first invoice!
                                        </td>
                                    </tr>
                                ) : (
                                    invoices.map((invoice) => (
                                        <tr key={invoice.id} className={`hover:bg-gray-50 dark:hover:bg-slate-600 ${selectedInvoices.includes(invoice.id) ? 'bg-purple-50 dark:bg-purple-900/20' : ''}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input 
                                                    type="checkbox"
                                                    checked={selectedInvoices.includes(invoice.id)}
                                                    onChange={() => toggleInvoiceSelection(invoice.id)}
                                                    disabled={invoice.status === 'paid'}
                                                    className="rounded border-gray-300 disabled:opacity-50"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                                                {invoice.invoice_number}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                                {invoice.ref_number || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                                {invoice.project?.name || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                                {invoice.billing_month} {invoice.billing_year}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                                                {formatCurrency(invoice.total_amount)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(invoice.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <div className="flex items-center gap-2">
                                                    <button 
                                                        onClick={() => handleViewInvoice(invoice)}
                                                        className="p-1 text-blue-600 hover:text-blue-800"
                                                        title="View"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDelete(invoice.id)}
                                                        className="p-1 text-red-600 hover:text-red-800"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Generate Invoice Modal */}
            {showGenerateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-600">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                                Generate Invoice from Lifts
                            </h2>
                        </div>
                        <div className="p-6">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Select Project
                                </label>
                                <select
                                    value={selectedProject || ''}
                                    onChange={(e) => handleProjectSelect(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                >
                                    <option value="">Select a project...</option>
                                    {projects.map(project => (
                                        <option key={project.id} value={project.id}>
                                            {project.name}
                                        </option>
                                    ))}
                                </select>
                                {projectError && (
                                    <p className="mt-1 text-sm text-red-600">{projectError}</p>
                                )}
                            </div>

                            {projectLifts.length > 0 && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Select Lifts ({selectedLifts.length} selected)
                                    </label>
                                    <div className="border border-gray-200 dark:border-slate-600 rounded-lg max-h-60 overflow-y-auto">
                                        {projectLifts.map(lift => (
                                            <div 
                                                key={lift.id}
                                                className={`p-3 border-b border-gray-200 dark:border-slate-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-600 ${
                                                    selectedLifts.includes(lift.id) ? 'bg-indigo-50 dark:bg-indigo-900' : ''
                                                }`}
                                                onClick={() => toggleLiftSelection(lift.id)}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-medium text-gray-800 dark:text-gray-200">{lift.name}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {lift.location} • {lift.brand?.name || 'N/A'} • {lift.capacity}kg
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-medium text-gray-800 dark:text-gray-200">
                                                            {formatCurrency(lift.monthly_rate)}/month
                                                        </p>
                                                        <p className="text-sm text-gray-500">Qty: {lift.quantity}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedLifts.length > 0 && (
                                <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Estimated Total:</span>
                                        <span className="text-xl font-bold text-indigo-600">
                                            {formatCurrency(
                                                projectLifts
                                                    .filter(l => selectedLifts.includes(l.id))
                                                    .reduce((sum, l) => sum + (l.monthly_rate || 0) * (l.quantity || 1), 0)
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-6 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowGenerateModal(false);
                                    setSelectedProject(null);
                                    setSelectedLifts([]);
                                }}
                                className="px-4 py-2 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleGenerateFromLifts}
                                disabled={generating || selectedLifts.length === 0}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                            >
                                {generating ? (
                                    <><RefreshCw className="w-4 h-4 mr-2 inline animate-spin" /> Generating...</>
                                ) : (
                                    'Generate Invoice'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Invoice Modal */}
            {showViewModal && selectedInvoice && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-600">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                                        Invoice {selectedInvoice.invoice_number}
                                    </h2>
                                    <p className="text-sm text-gray-500">{selectedInvoice.ref_number}</p>
                                </div>
                                {getStatusBadge(selectedInvoice.status)}
                            </div>
                        </div>
                        
                        <div className="p-6">
                            {/* Invoice Header Info */}
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Bill To:</h3>
                                    <p className="text-gray-600 dark:text-gray-400">{selectedInvoice.client?.name}</p>
                                    <p className="text-sm text-gray-500">{selectedInvoice.project?.name}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-500">Date: {formatDate(selectedInvoice.invoice_date)}</p>
                                    <p className="text-sm text-gray-500">Billing: {selectedInvoice.billing_month} {selectedInvoice.billing_year}</p>
                                </div>
                            </div>

                            {/* Work Order References */}
                            {(selectedInvoice.work_order_no || selectedInvoice.noa_ref || selectedInvoice.coa_ref) && (
                                <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                                    <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">References:</h3>
                                    <div className="grid grid-cols-3 gap-2 text-sm">
                                        {selectedInvoice.work_order_no && (
                                            <div>
                                                <span className="text-gray-500">Work Order:</span>
                                                <p className="text-gray-800 dark:text-gray-200">{selectedInvoice.work_order_no}</p>
                                            </div>
                                        )}
                                        {selectedInvoice.noa_ref && (
                                            <div>
                                                <span className="text-gray-500">NOA:</span>
                                                <p className="text-gray-800 dark:text-gray-200">{selectedInvoice.noa_ref}</p>
                                            </div>
                                        )}
                                        {selectedInvoice.coa_ref && (
                                            <div>
                                                <span className="text-gray-500">Contract:</span>
                                                <p className="text-gray-800 dark:text-gray-200">{selectedInvoice.coa_ref}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Items Table */}
                            <table className="w-full mb-6">
                                <thead className="bg-gray-50 dark:bg-slate-600">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">#</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qty</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Rate</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                    {selectedInvoice.items?.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{index + 1}</td>
                                            <td className="px-4 py-2">
                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{item.description}</p>
                                                <p className="text-xs text-gray-500">
                                                    {item.brand && `${item.brand} • `}
                                                    {item.capacity && `${item.capacity}kg • `}
                                                    {item.location && item.location}
                                                </p>
                                            </td>
                                            <td className="px-4 py-2 text-right text-sm text-gray-600 dark:text-gray-300">{item.quantity}</td>
                                            <td className="px-4 py-2 text-right text-sm text-gray-600 dark:text-gray-300">{formatCurrency(item.rate)}</td>
                                            <td className="px-4 py-2 text-right text-sm font-medium text-gray-800 dark:text-gray-200">{formatCurrency(item.total_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Totals */}
                            <div className="flex justify-end">
                                <div className="w-64">
                                    <div className="flex justify-between py-2">
                                        <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                                        <span className="font-medium text-gray-800 dark:text-gray-200">{formatCurrency(selectedInvoice.subtotal)}</span>
                                    </div>
                                    <div className="flex justify-between py-2 border-t border-gray-200 dark:border-slate-600">
                                        <span className="font-bold text-gray-800 dark:text-gray-200">Total:</span>
                                        <span className="font-bold text-indigo-600">{formatCurrency(selectedInvoice.total_amount)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Amount in Words */}
                            {selectedInvoice.amount_in_words && (
                                <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        <span className="font-medium">In Words:</span> {selectedInvoice.amount_in_words}
                                    </p>
                                </div>
                            )}

                            {/* Notes */}
                            {selectedInvoice.notes && (
                                <div className="mt-4">
                                    <p className="text-sm text-gray-500">Note: {selectedInvoice.notes}</p>
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    setSelectedInvoice(null);
                                }}
                                className="px-4 py-2 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Merge Invoices Modal */}
            {showMergeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-600">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                                Merge Invoices
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Combining {selectedInvoices.length} invoices into a single invoice
                            </p>
                        </div>
                        
                        <div className="p-6">
                            {/* Selected Invoices Summary */}
                            <div className="mb-6">
                                <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Selected Invoices:</h3>
                                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                                    {invoices.filter(inv => selectedInvoices.includes(inv.id)).map(inv => (
                                        <div key={inv.id} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-600 last:border-0">
                                            <div>
                                                <span className="font-medium text-gray-800 dark:text-gray-200">{inv.invoice_number}</span>
                                                <span className="text-sm text-gray-500 ml-2">({inv.billing_month} {inv.billing_year})</span>
                                            </div>
                                            <span className="font-medium text-gray-800 dark:text-gray-200">{formatCurrency(inv.total_amount)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-2 flex justify-between items-center">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Total Amount:</span>
                                    <span className="text-xl font-bold text-indigo-600">
                                        {formatCurrency(
                                            invoices
                                                .filter(inv => selectedInvoices.includes(inv.id))
                                                .reduce((sum, inv) => sum + (inv.total_amount || 0), 0)
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Merge Form */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Invoice Date
                                    </label>
                                    <input
                                        type="date"
                                        value={mergeFormData.invoice_date}
                                        onChange={(e) => setMergeFormData({...mergeFormData, invoice_date: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Billing Month
                                        </label>
                                        <select
                                            value={mergeFormData.billing_month}
                                            onChange={(e) => setMergeFormData({...mergeFormData, billing_month: e.target.value})}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                        >
                                            <option value="">Select...</option>
                                            {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Month #
                                        </label>
                                        <select
                                            value={mergeFormData.billing_month_num}
                                            onChange={(e) => setMergeFormData({...mergeFormData, billing_month_num: parseInt(e.target.value)})}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                        >
                                            {[...Array(12)].map((_, i) => (
                                                <option key={i+1} value={i+1}>{i+1}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Year
                                        </label>
                                        <input
                                            type="number"
                                            value={mergeFormData.billing_year}
                                            onChange={(e) => setMergeFormData({...mergeFormData, billing_year: parseInt(e.target.value)})}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="markAsMerged"
                                        checked={mergeFormData.mark_as_merged}
                                        onChange={(e) => setMergeFormData({...mergeFormData, mark_as_merged: e.target.checked})}
                                        className="rounded border-gray-300"
                                    />
                                    <label htmlFor="markAsMerged" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                        Mark source invoices as merged
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowMergeModal(false);
                                    setSelectedInvoices([]);
                                }}
                                className="px-4 py-2 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMergeInvoices}
                                disabled={merging || !mergeFormData.billing_month}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 flex items-center"
                            >
                                {merging ? (
                                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Merging...</>
                                ) : (
                                    <><Merge className="w-4 h-4 mr-2" /> Merge Invoices</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Invoices;
